import React, { useEffect, useState } from 'react';
import { X, Star, Heart, Check } from 'lucide-react';
import { API_BASE_URL } from '../../api';
import { getSelectedVariant, getVariantImages, getDisplayColors, isInStock } from '../../utils/productHelpers';

const renderStars = (rating) => (
  Array.from({ length: 5 }, (_, i) => (
    <Star key={i} className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-brass-deep fill-current' : 'text-line'}`} />
  ))
);

const ProductQuickView = ({ product, onClose, onAddToCart, isWishlisted, onToggleWishlist }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedColorOption, setSelectedColorOption] = useState(null);

  const baseVariant = product ? getSelectedVariant(product) : null;
  const colors = product ? getDisplayColors(product) : [];

  useEffect(() => {
    if (!product) return;
    const variant = getSelectedVariant(product);
    setSelectedImage(variant.image);
    setSelectedColorOption(null);
  }, [product]);

  if (!product) return null;

  const active = selectedColorOption || baseVariant;
  const gallery = selectedColorOption?.images?.length
    ? getVariantImages(selectedColorOption, API_BASE_URL)
    : getVariantImages(baseVariant, API_BASE_URL);

  const handleSelectColor = (option) => {
    const matchingVariant = (product.variants || []).find(
      (v) => v.color === option.color || v.colorCode === option.colorCode
    ) || baseVariant;

    const merged = {
      ...matchingVariant,
      color: option.color,
      colorName: option.colorName,
      colorCode: option.colorCode,
      imageId: option.imageId,
      image: option.imageUrl || matchingVariant.image,
      images: matchingVariant.images || matchingVariant.variantImages,
    };
    setSelectedColorOption(merged);
    setSelectedImage(merged.image);
  };

  const handleAdd = () => {
    onAddToCart({
      ...product,
      selectedColor: active.color || active.colorName,
      selectedColorCode: active.colorCode,
      selectedImageId: active.imageId,
      colorName: active.colorName || active.color,
    });
  };

  const onSale = active.originalPrice > active.price;

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-md shadow-2xl" data-lenis-prevent>
        <button onClick={onClose} className="absolute top-5 right-5 z-10 p-3 bg-paper rounded-full hover:bg-line transition-colors" aria-label="Close">
          <X className="w-5 h-5 text-ink" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Gallery */}
          <div className="p-6 sm:p-10">
            <div className="relative aspect-[4/5] bg-paper rounded-sm overflow-hidden mb-4">
              <img
                src={selectedImage || gallery[0]}
                alt={product.baseName || product.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/images/placeholder.jpg'; }}
              />
              {onSale && <span className="absolute top-3 left-3 dpc-label bg-accent text-paper px-2.5 py-1 rounded-full text-[10px]">Sale</span>}
              {!isInStock(active) && (
                <div className="absolute inset-0 bg-ink/55 flex items-center justify-center">
                  <span className="dpc-label text-paper">Out of stock</span>
                </div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(img)}
                    className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 ${selectedImage === img ? 'border-accent' : 'border-transparent'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = '/images/placeholder.jpg'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-6 sm:p-10 md:pl-0 flex flex-col">
            <p className="dpc-label text-ink-faint mb-2">{product.brand}</p>
            <h2 className="font-display text-3xl text-ink mb-2 leading-tight">{product.baseName || product.name}</h2>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-0.5">{renderStars(active.rating || 0)}</div>
              <span className="text-sm text-ink-faint">({active.reviews || 0} reviews)</span>
            </div>

            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-2xl font-display text-ink">KSh {Number(active.price || 0).toLocaleString()}</span>
              {onSale && (
                <>
                  <span className="text-ink-faint line-through">KSh {Number(active.originalPrice).toLocaleString()}</span>
                  <span className="dpc-label text-accent">Save KSh {Number(active.originalPrice - active.price).toLocaleString()}</span>
                </>
              )}
            </div>

            {product.description && (
              <p className="text-sm text-ink-soft leading-relaxed mb-6">{product.description}</p>
            )}

            {colors.length > 0 && (
              <div className="mb-6">
                <p className="dpc-label text-ink-faint mb-2.5">Colour — {active.colorName || active.color}</p>
                <div className="flex flex-wrap gap-2.5">
                  {colors.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleSelectColor(c)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        (active.colorCode === c.colorCode) ? 'border-ink scale-110' : 'border-line'
                      }`}
                      style={{ background: c.colorCode }}
                      title={c.colorName}
                    />
                  ))}
                </div>
              </div>
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6 text-sm">
              {product.material && (<><dt className="text-ink-faint">Material</dt><dd className="text-ink">{product.material}</dd></>)}
              {active.dimensions && (<><dt className="text-ink-faint">Dimensions</dt><dd className="text-ink">{active.dimensions}</dd></>)}
              {product.closure && (<><dt className="text-ink-faint">Closure</dt><dd className="text-ink">{product.closure}</dd></>)}
              <dt className="text-ink-faint">Availability</dt>
              <dd className="text-ink">{!isInStock(active) ? 'Out of stock' : 'In stock'}</dd>
            </dl>

            {Array.isArray(product.features) && product.features.length > 0 && (
              <ul className="mb-8 space-y-1.5">
                {product.features.slice(0, 6).map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-ink-soft">
                    <Check className="w-3.5 h-3.5 text-accent flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-auto flex items-center gap-3">
              <button
                onClick={handleAdd}
                disabled={!isInStock(active)}
                className="flex-1 bg-ink text-paper dpc-label py-3.5 rounded-full hover:bg-accent transition-colors disabled:opacity-40"
              >
                {!isInStock(active) ? 'Unavailable' : 'Add to bag'}
              </button>
              <button
                onClick={() => onToggleWishlist(product.id)}
                className="p-3.5 border border-line rounded-full hover:border-accent transition-colors"
                aria-label="Toggle wishlist"
              >
                <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-accent text-accent' : 'text-ink-faint'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuickView;
